import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDateRelative } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";
import { Trophy, XCircle, CheckCircle2, Star, Users, TrendingUp, PoundSterling } from "lucide-react";

function Stars({ rating }) {
    if (!rating) return <span className="text-xs text-muted-foreground italic">Unrated</span>;
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    className="w-3.5 h-3.5"
                    strokeWidth={1.5}
                    style={n <= rating ? { fill: "hsl(var(--gold))", color: "hsl(var(--gold))" } : { color: "hsl(var(--muted-foreground))" }}
                />
            ))}
            <span className="text-xs font-mono text-muted-foreground ml-1">{rating.toFixed ? rating.toFixed(1) : rating}</span>
        </div>
    );
}

export default function Outcomes() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [brokers, setBrokers] = useState([]);
    const [brokerFilter, setBrokerFilter] = useState("all");

    const refresh = async (filter = "all") => {
        setLoading(true);
        const q = filter !== "all" ? `?broker=${encodeURIComponent(filter)}` : "";
        const [s, b] = await Promise.all([
            api.get(`/outcomes/summary${q}`),
            api.get(`/outcomes/brokers`),
        ]);
        setData(s.data);
        setBrokers(b.data);
        setLoading(false);
    };

    useEffect(() => { refresh(brokerFilter); /* eslint-disable-next-line */ }, [brokerFilter]);

    if (loading || !data) return <div className="p-8 text-sm text-muted-foreground">Loading outcomes…</div>;

    return (
        <div data-testid="outcomes-page">
            <PageHeader title="Outcomes" subtitle="Performance across completed and lost deals.">
                <Select value={brokerFilter} onValueChange={setBrokerFilter}>
                    <SelectTrigger className="w-52" data-testid="broker-filter-select">
                        <SelectValue placeholder="Filter by broker…" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All brokers</SelectItem>
                        {brokers.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                </Select>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-8">
                <motion.div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                    initial="hidden" animate="show"
                    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
                >
                    {[
                        { label: "Completed", value: data.completed_count, icon: Trophy, testid: "stat-completed" },
                        { label: "Lost", value: data.lost_count, icon: XCircle, testid: "stat-lost" },
                        { label: "Win rate", value: `${data.win_rate}%`, icon: CheckCircle2, testid: "stat-win-rate", mono: true },
                        { label: "Fees earned", value: fmtMoney(data.total_fees_earned), icon: PoundSterling, testid: "stat-fees-earned", mono: true, accent: "gold" },
                    ].map((s) => (
                        <motion.div key={s.testid}
                            variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                        >
                            <StatCard {...s} />
                        </motion.div>
                    ))}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 border border-border rounded-[6px] bg-white py-4 px-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Users className="w-4 h-4 text-gold" strokeWidth={1.75} />
                            <h2 className="font-display text-lg font-bold tracking-tight">Broker performance</h2>
                        </div>
                        {data.broker_stats.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No closed deals yet.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                        <th className="text-left py-2">Broker</th>
                                        <th className="text-right py-2">Won</th>
                                        <th className="text-right py-2">Lost</th>
                                        <th className="text-right py-2">Win %</th>
                                        <th className="text-right py-2">Fees</th>
                                        <th className="text-right py-2">Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.broker_stats.map((b) => (
                                        <tr key={b.name} className="border-b border-border/60" data-testid={`broker-row-${b.name}`}>
                                            <td className="py-3 font-medium">{b.name}</td>
                                            <td className="py-3 text-right font-mono text-green-700">{b.completed}</td>
                                            <td className="py-3 text-right font-mono text-red-700">{b.lost}</td>
                                            <td className="py-3 text-right font-mono">{b.win_rate}%</td>
                                            <td className="py-3 text-right font-mono">{fmtMoney(b.fees_earned)}</td>
                                            <td className="py-3 text-right"><Stars rating={b.avg_rating} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="border border-border rounded-[6px] bg-white py-4 px-4">
                        <div className="flex items-center gap-2 mb-3">
                            <XCircle className="w-4 h-4 text-destructive" strokeWidth={1.75} />
                            <h2 className="font-display text-lg font-bold tracking-tight">Why deals were lost</h2>
                        </div>
                        {data.lost_reasons.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No lost deals yet.</p>
                        ) : (
                            <ul className="space-y-2">
                                {data.lost_reasons.map((r) => (
                                    <li key={r.reason} className="flex items-center justify-between text-sm">
                                        <span className="truncate">{r.reason}</span>
                                        <Badge variant="outline" className="font-mono">{r.count}</Badge>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <Tabs defaultValue="completed">
                    <TabsList className="mb-4" data-testid="outcome-tabs">
                        <TabsTrigger value="completed" data-testid="outcome-tab-completed">
                            <Trophy className="w-3.5 h-3.5 mr-1.5" /> Completed ({data.completed_count})
                        </TabsTrigger>
                        <TabsTrigger value="lost" data-testid="outcome-tab-lost">
                            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Lost ({data.lost_count})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="completed">
                        <DealList deals={data.completed_deals} type="completed" emptyText="No completed deals yet." />
                    </TabsContent>
                    <TabsContent value="lost">
                        <DealList deals={data.lost_deals} type="lost" emptyText="No lost deals — great work!" />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function DealList({ deals, type, emptyText }) {
    if (deals.length === 0) {
        return (
            <div className="border border-dashed border-border rounded-[6px] py-4 px-4 text-center">
                <p className="text-sm text-muted-foreground">{emptyText}</p>
            </div>
        );
    }
    return (
        <div className="border border-border rounded-md bg-white overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                        <th className="text-left px-4 py-3">Company</th>
                        <th className="text-left px-4 py-3">Sector</th>
                        <th className="text-left px-4 py-3">Broker</th>
                        <th className="text-right px-4 py-3">Deal size</th>
                        {type === "completed" && <th className="text-right px-4 py-3">Fee</th>}
                        {type === "completed" && <th className="text-left px-4 py-3">Rating</th>}
                        {type === "lost" && <th className="text-left px-4 py-3">Reason</th>}
                        <th className="text-left px-4 py-3">Notes</th>
                        <th className="text-right px-4 py-3">Closed</th>
                    </tr>
                </thead>
                <tbody>
                    {deals.map((d) => (
                        <motion.tr
                            key={d.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25 }}
                            className="border-b border-border/60 hover:bg-muted/30"
                            data-testid={`outcome-row-${d.id}`}
                        >
                            <td className="px-4 py-3 font-medium">
                                <Link to={`/deals/${d.id}`} className="hover:text-navy underline underline-offset-2">{d.company_name}</Link>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{d.sector || "—"}</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.lead_broker || d.owner_name || "—"}</td>
                            <td className="px-4 py-3 text-right font-mono">{fmtMoney(d.deal_size, d.currency)}</td>
                            {type === "completed" && (
                                <td className="px-4 py-3 text-right font-mono text-gold">
                                    {d.fee ? fmtMoney(d.fee, d.currency) : "—"}
                                    {d.fee_type === "percentage" && d.fee_percentage && (
                                        <div className="text-[10px] text-muted-foreground">{d.fee_percentage}%</div>
                                    )}
                                </td>
                            )}
                            {type === "completed" && (
                                <td className="px-4 py-3"><Stars rating={d.broker_rating} /></td>
                            )}
                            {type === "lost" && (
                                <td className="px-4 py-3 text-xs">
                                    {d.outcome_reason ? (
                                        <Badge variant="outline" className="text-[10px]">{d.outcome_reason}</Badge>
                                    ) : <span className="text-muted-foreground italic">Not set</span>}
                                </td>
                            )}
                            <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">{d.outcome_notes || "—"}</td>
                            <td className="px-4 py-3 text-right text-xs font-mono text-muted-foreground">{fmtDateRelative(d.updated_at)}</td>
                        </motion.tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
